import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const FIELD_CLASS =
  'h-12 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] px-[15px] py-[13px] text-[15px] text-[#EAEEF6] placeholder:text-[rgba(234,238,246,0.30)] focus-visible:border-[#7DB1FF] focus-visible:ring-[3px] focus-visible:ring-[rgba(125,177,255,0.16)]';
const PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-[#EAEEF6] text-[15px] font-semibold text-[#070A12] transition-transform duration-150 hover:-translate-y-px hover:bg-[#EAEEF6] hover:shadow-[0_10px_32px_rgba(125,177,255,0.22)] disabled:opacity-60 disabled:hover:translate-y-0';

/// The second-factor challenge shown after a password login when the account
/// has MFA enrolled. Previously this lived inline inside `LoginPage`'s render;
/// extracting it to its own component keeps the login surface readable and
/// gives the challenge a single, testable home.
///
/// NOTE: this is a self-contained COMPONENT, not a router route — the MFA token
/// captured at login lives in the in-memory `AuthProvider` context, and a real
/// route navigation (or refresh) would drop it. `LoginPage` renders this when
/// `requiresMfa` is true; the AuthProvider context is shared, so `verifyMfa`
/// here completes the same in-flight challenge.
export default function MfaChallengePage() {
  const { verifyMfa } = useAuth();
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaCode);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleMfa}>
      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <p className="mb-3 text-sm text-[rgba(136,146,168,0.8)]">
        Enter the 6-digit code from your authenticator app.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mfa-code" className="text-[rgba(136,146,168,0.8)]">MFA Code</Label>
        <Input
          id="mfa-code"
          required
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          maxLength={6}
          pattern="[0-9]{6}"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          className={cn(FIELD_CLASS, 'text-center tracking-[0.3em]')}
        />
      </div>
      <Button
        type="submit"
        disabled={loading || mfaCode.length !== 6}
        className={cn('mt-4 h-12 w-full text-[15px] font-semibold', PRIMARY_BUTTON)}
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify'}
      </Button>
    </form>
  );
}
